import React from "react";

/**
 * Button that can render as:
 * - <button> (default)
 * - <a> when `href` is provided
 * - custom link component when `as` is provided (e.g., React Router/Next Link)
 *
 * Props:
 * - isLoading, disabled, className, onClick
 * - href, target, rel (for anchors)
 * - as: component to render instead (e.g., Link from react-router-dom or next/link)
 * - to: for router links (passed through)
 * - variant: "primary" | "secondary" | "accent" | "gray"
 */
const Button = React.forwardRef(
  (
    {
      children,
      onClick,
      disabled = false,
      className = "",
      isLoading = false,
      href,
      target,
      rel,
      as: As,   // e.g., import { Link } from 'react-router-dom'; <Button as={Link} to="/x" />
      to,       // for router links
      type = "button", // only matters for real <button>
      ...rest
    },
    ref
  ) => {
    const baseStyles =
      "font-bold py-2 px-4 rounded transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all inline-flex items-center";

    const typeStyles = {
      primary:
        "bg-primary hover:bg-primary-hover active:bg-primary-active text-white focus:ring-primary",
      secondary:
        "bg-secondary hover:bg-secondary-hover active:bg-secondary-active text-white focus:ring-secondary",
      accent:
        "bg-accent hover:bg-accent-hover active:bg-accent-active text-text focus:ring-accent",
      gray:
        "bg-gray-500 hover:bg-gray-600 active:bg-gray-700 text-white focus:ring-gray-500",
    };

    const disabledStyles = "bg-gray-300 text-gray-600 cursor-not-allowed";
    const classes = `${baseStyles} ${
      disabled ? disabledStyles : typeStyles[type] || typeStyles.primary
    } ${className}`.trim();

    const content = (
      <>
        {isLoading && (
          <svg
            className="w-5 h-5 animate-spin text-current mr-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C3.373 0 0 3.373 0 12h4zm2 5.291A7.972 7.972 0 014 12H0c0 4.528 2.94 8.37 7 9.709l-1-2.418z"
            />
          </svg>
        )}
        {children}
      </>
    );

    // If a custom component is provided (e.g., React Router <Link/> or Next.js <Link/>)
    if (As) {
      return (
        <As
          ref={ref}
          className={classes}
          onClick={disabled ? (e) => e.preventDefault() : onClick}
          aria-disabled={disabled || undefined}
          to={to}
          href={href}
          {...rest}
        >
          {content}
        </As>
      );
    }

    // If href is present, render an anchor
    if (href) {
      const computedRel =
        target === "_blank"
          ? rel
            ? rel
            : "noopener noreferrer"
          : rel;

      return (
        <a
          ref={ref}
          className={classes}
          href={disabled ? undefined : href}
          target={target}
          rel={computedRel}
          onClick={disabled ? (e) => e.preventDefault() : onClick}
          aria-disabled={disabled || undefined}
          {...rest}
        >
          {content}
        </a>
      );
    }

    // Default: real button
    return (
      <button
        ref={ref}
        type={type}
        className={classes}
        onClick={onClick}
        disabled={disabled}
        {...rest}
      >
        {content}
      </button>
    );
  }
);

export default Button;
